import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { eventApi, businessApi, serviceApi, staffApi } from '../services/api';
import { Event, Business } from '../types';
import { Modal } from '../components/Modal';

interface EventForm {
  businessId: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  serviceId: string;
  staffId: string;
}

export function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState('');

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<EventForm>({
    defaultValues: { maxCapacity: 10, description: '', serviceId: '', staffId: '' },
  });

  const watchedBusiness = watch('businessId');

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (watchedBusiness && watchedBusiness !== selectedBusiness) {
      setSelectedBusiness(watchedBusiness);
      loadServicesAndStaff(watchedBusiness);
    }
  }, [watchedBusiness]);

  const fetchData = async () => {
    try {
      const bRes = await businessApi.getMyBusinesses();
      const bList = bRes.data.data.businesses || [];
      setBusinesses(bList);
      if (bList.length > 0) {
        const eRes = await eventApi.getBusinessEvents(bList[0].id);
        setEvents(eRes.data.data.events || []);
        await loadServicesAndStaff(bList[0].id);
      }
    } catch { toast.error('Error cargando datos'); }
    finally { setIsLoading(false); }
  };

  const loadServicesAndStaff = async (businessId: string) => {
    try {
      const [sRes, stRes] = await Promise.all([
        serviceApi.getBusinessServices(businessId),
        staffApi.getBusinessStaff(businessId),
      ]);
      setServices(sRes.data.data.services || []);
      setStaff(stRes.data.data.staff || []);
    } catch {}
  };

  const onSubmit = async (data: EventForm) => {
    try {
      const payload = {
        businessId: data.businessId,
        title: data.title,
        description: data.description || undefined,
        startTime: new Date(data.startTime).toISOString(),
        endTime: new Date(data.endTime).toISOString(),
        maxCapacity: Number(data.maxCapacity),
        serviceId: data.serviceId || undefined,
        staffId: data.staffId || undefined,
      };
      if (editingEvent) {
        await eventApi.updateEvent(editingEvent.id, payload as any);
        toast.success('Evento actualizado');
      } else {
        await eventApi.createEvent(payload as any);
        toast.success('Evento creado');
      }
      setIsModalOpen(false);
      setEditingEvent(null);
      reset();
      fetchData();
    } catch { toast.error('Error al guardar el evento'); }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    reset({
      businessId: event.businessId,
      title: event.title,
      description: event.description || '',
      startTime: new Date(event.startTime).toISOString().slice(0, 16),
      endTime: new Date(event.endTime).toISOString().slice(0, 16),
      maxCapacity: event.maxCapacity || 10,
      serviceId: event.serviceId || '',
      staffId: event.staffId || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este evento?')) return;
    try {
      await eventApi.deleteEvent(id);
      toast.success('Evento eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddNew = () => {
    setEditingEvent(null);
    reset({
      businessId: businesses[0]?.id || '',
      title: '', description: '',
      startTime: '', endTime: '',
      maxCapacity: 10, serviceId: '', staffId: '',
    });
    setIsModalOpen(true);
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary-900">Eventos</h1>
        <button onClick={handleAddNew} className="btn-primary flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />Nuevo evento
        </button>
      </div>

      {events.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => (
            <div key={event.id} className="card p-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-secondary-900">{event.title}</h3>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  {event.maxCapacity} plazas
                </span>
              </div>
              {event.description && <p className="text-sm text-secondary-600 mb-3 line-clamp-2">{event.description}</p>}
              <div className="space-y-1 text-sm text-secondary-600 mb-4">
                <p>🗓 <span className="font-medium">Inicio:</span> {new Date(event.startTime).toLocaleString('es-ES')}</p>
                <p>🏁 <span className="font-medium">Fin:</span> {new Date(event.endTime).toLocaleString('es-ES')}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleEdit(event)} className="flex-1 btn-secondary text-sm">
                  <PencilIcon className="h-4 w-4 mr-1 inline" />Editar
                </button>
                <button onClick={() => handleDelete(event.id)} className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-4xl mb-4">📅</p>
          <p className="text-secondary-500 mb-4">No tienes eventos aún.</p>
          <button onClick={handleAddNew} className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />Crear primer evento
          </button>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingEvent(null); }} title={editingEvent ? 'Editar evento' : 'Nuevo evento'} maxWidth="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!editingEvent && (
            <div>
              <label className="label">Negocio *</label>
              <select {...register('businessId', { required: true })} className="input">
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">Título del evento *</label>
            <input {...register('title', { required: 'El título es obligatorio' })} type="text" className="input" placeholder="Ej: Clase de yoga, Corte de pelo..." />
            {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="label">Descripción</label>
            <textarea {...register('description')} className="input" rows={2} placeholder="Descripción del evento..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Fecha y hora inicio *</label>
              <input {...register('startTime', { required: 'Obligatorio' })} type="datetime-local" className="input" />
              {errors.startTime && <p className="text-sm text-red-600 mt-1">{errors.startTime.message}</p>}
            </div>
            <div>
              <label className="label">Fecha y hora fin *</label>
              <input {...register('endTime', { required: 'Obligatorio' })} type="datetime-local" className="input" />
              {errors.endTime && <p className="text-sm text-red-600 mt-1">{errors.endTime.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">Plazas máximas</label>
            <input {...register('maxCapacity', { valueAsNumber: true })} type="number" min={1} className="input" />
          </div>

          {services.length > 0 && (
            <div>
              <label className="label">Servicio asociado (opcional)</label>
              <select {...register('serviceId')} className="input">
                <option value="">— Sin servicio —</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.price}€)</option>)}
              </select>
            </div>
          )}

          {staff.length > 0 && (
            <div>
              <label className="label">Personal asignado (opcional)</label>
              <select {...register('staffId')} className="input">
                <option value="">— Sin asignar —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
              </select>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={() => { setIsModalOpen(false); setEditingEvent(null); }} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Guardando...' : editingEvent ? 'Actualizar' : 'Crear evento'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
